// src/plugins/sales-forecast/components/SaveForecastButton.tsx
import React, { useMemo, useState } from "react";
import type { Params } from "./ControlPanel";
import type { CloneEntry } from "./CloneNeighborhoodControl";
import type { ForecastRecord } from "./MarketForecastPanel";
import { LightDarkModeSwitch } from "./LightDarkModeSwitch";
import "../../sales-forecast/styles/theme.css";

const api_base = process.env.REACT_APP_API_BASE;

type SaveForecastButtonProps = {
  params: Params;
  cluster: number;
  forecastData: ForecastRecord[];
  pipelineData: any[] | null;
  selectedClone: CloneEntry | null;
  label?: string;
  disabled?: boolean;
  onSaved?: (result: any) => void;
  // ── Theme props (passed down from index.tsx via MarketForecastPanel) ──
  theme: "light" | "dark";
  onToggleTheme: () => void;
};

function safeIsoDate(d: Date = new Date()): string {
  return d.toISOString().split("T")[0];
}

function makeClientForecastId(): string {
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `fc_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export const SaveForecastButton: React.FC<SaveForecastButtonProps> = ({
  params,
  cluster,
  forecastData,
  pipelineData,
  selectedClone,
  label,
  disabled,
  onSaved,
  theme,
  onToggleTheme,
}) => {
  const textColor = "var(--text-color)";
  const mutedText = "var(--muted-text-color)";
  const inputBg = "var(--subcard-bg)";
  const borderColor = "var(--border-color)";
  const accentColor = "var(--accent-color)";

  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);
  const [refreshErr, setRefreshErr] = useState<string | null>(null);

  const isSaveDisabled = useMemo(() => {
    if (disabled) return true;
    if (saving || refreshing) return true;
    if (!forecastData || forecastData.length === 0) return true;
    if (!api_base) return true;
    return false;
  }, [disabled, saving, refreshing, forecastData]);

  const isRefreshDisabled = useMemo(() => {
    if (disabled) return true;
    if (saving || refreshing) return true;
    if (!api_base) return true;
    return false;
  }, [disabled, saving, refreshing]);

  const derivedLabel = useMemo(() => {
    if (label && label.trim()) return label.trim();

    const cloneTitle =
      selectedClone?.title ??
      (params as any)?.neighborhoodTitle ??
      (params as any)?.neighborhoodID ??
      "Forecast";

    const start = (params as any)?.cloneDate
      ? String((params as any).cloneDate)
      : safeIsoDate();

    return `${cloneTitle} — ${start} — Cluster ${cluster}`;
  }, [label, selectedClone, params, cluster]);

  const handleSave = async () => {
    setErrMsg(null);
    setSavedMsg(null);

    if (!api_base) {
      setErrMsg("REACT_APP_API_BASE is not set.");
      return;
    }
    if (!forecastData?.length) {
      setErrMsg("No forecast data to save yet.");
      return;
    }

    setSaving(true);

    const payload = {
      forecast_id: makeClientForecastId(),
      label: derivedLabel,
      created_at: new Date().toISOString(),
      cluster,
      clone: {
        id: selectedClone?.id ?? (params as any)?.neighborhoodID ?? null,
        title: selectedClone?.title ?? (params as any)?.neighborhoodTitle ?? null,
        cloneType: selectedClone?.cloneType ?? (params as any)?.cloneType ?? null,
        status: selectedClone?.status ?? (params as any)?.status ?? null,
      },
      params,
      records: forecastData,
      pipeline: pipelineData ?? null,
    };

    try {
      const resp = await fetch(`${api_base}/save-forecast`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        let details = "";
        try {
          const j = await resp.json();
          const msg = j?.detail ?? j?.error;
          details = typeof msg === "string" ? ` — ${msg}` : "";
        } catch {}
        throw new Error(`Error ${resp.status}: ${resp.statusText}${details}`);
      }

      const result = await resp.json().catch(() => ({}));
      setSavedMsg("Saved!");
      onSaved?.(result);
      window.setTimeout(() => setSavedMsg(null), 2500);
    } catch (e: any) {
      setErrMsg(e?.message || "Failed to save forecast.");
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshErr(null);
    setRefreshMsg(null);

    if (!api_base) {
      setRefreshErr("REACT_APP_API_BASE is not set.");
      return;
    }

    setRefreshing(true);

    try {
      const resp = await fetch(`${api_base}/refresh-forecast-summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!resp.ok) {
        let details = "";
        try {
          const j = await resp.json();
          const msg = j?.detail ?? j?.error;
          details = typeof msg === "string" ? ` — ${msg}` : "";
        } catch {}
        throw new Error(`Error ${resp.status}: ${resp.statusText}${details}`);
      }

      const result = await resp.json().catch(() => ({}));
      const secs = typeof result?.seconds === "number" ? result.seconds : null;

      setRefreshMsg(secs != null ? `Refreshed (${secs}s)` : "Refreshed!");
      window.setTimeout(() => setRefreshMsg(null), 2500);
    } catch (e: any) {
      setRefreshErr(e?.message || "Refresh failed.");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className={`theme-${theme} flex items-center gap-2`} style={{ color: textColor }}>
      {/* Status messages */}
      {savedMsg && (
        <span className="text-xs" style={{ color: accentColor }}>
          {savedMsg}
        </span>
      )}
      {errMsg && (
        <span className="text-xs" style={{ color: "var(--error-text)" }} title={errMsg}>
          {errMsg.length > 60 ? `${errMsg.slice(0, 60)}…` : errMsg}
        </span>
      )}
      {refreshMsg && (
        <span className="text-xs" style={{ color: accentColor }}>
          {refreshMsg}
        </span>
      )}
      {refreshErr && (
        <span className="text-xs" style={{ color: "var(--error-text)" }} title={refreshErr}>
          {refreshErr.length > 60 ? `${refreshErr.slice(0, 60)}…` : refreshErr}
        </span>
      )}

      {/* Light/Dark mode toggle — just to the left of Refresh Data */}
      <LightDarkModeSwitch theme={theme} onToggle={onToggleTheme} />

      {/* Refresh button */}
      <button
        type="button"
        onClick={handleRefresh}
        disabled={isRefreshDisabled}
        className="transition-all duration-200"
        style={{
          backgroundColor: isRefreshDisabled ? (inputBg || "var(--subcard-bg)") : "transparent",
          color: isRefreshDisabled ? mutedText : textColor,
          border: `1px solid ${borderColor}`,
          borderRadius: "10px",
          padding: "0.45rem 0.85rem",
          fontSize: "0.9rem",
          fontWeight: 600,
          cursor: isRefreshDisabled ? "not-allowed" : "pointer",
          boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
          opacity: refreshing ? 0.9 : 1,
          whiteSpace: "nowrap",
        }}
        title={!api_base ? "REACT_APP_API_BASE is not set" : "Refresh summary MV"}
      >
        {refreshing ? "Refreshing…" : "Refresh Data"}
      </button>

      {/* Save button */}
      <button
        type="button"
        onClick={handleSave}
        disabled={isSaveDisabled}
        className="transition-all duration-200"
        style={{
          backgroundColor: isSaveDisabled ? (inputBg || "var(--subcard-bg)") : accentColor,
          color: isSaveDisabled ? mutedText : "#fff",
          border: `1px solid ${borderColor}`,
          borderRadius: "10px",
          padding: "0.45rem 0.85rem",
          fontSize: "0.9rem",
          fontWeight: 600,
          cursor: isSaveDisabled ? "not-allowed" : "pointer",
          boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
          opacity: saving ? 0.9 : 1,
          whiteSpace: "nowrap",
        }}
        title={
          !api_base
            ? "REACT_APP_API_BASE is not set"
            : !forecastData?.length
              ? "Run a forecast first"
              : "Save this forecast"
        }
      >
        {saving ? "Saving…" : "Save Forecast"}
      </button>
    </div>
  );
};